import { useCallback } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import Text from '../../../../../components/Text';
import { Triangle } from '../../../components/Triangle';
import StatusUI from '../../../components/StatusUI';
import HandingUI from '../../../components/HandingUI';
import { conditional, initValue } from '../../../../../utils';
import admissionUtil from '../../../../../utils/admissionUtil';
import { AckInfo, EditUI, PatientUI, WardUI } from '../../../components/BusStop/components';
import StyleButton from '../../../../../components/StyleButton';
import { useQuery } from '../../../../../utils/useQuery';
import { eadmissionAPI } from '../../../../../apis';
import { getPriorityInfo } from '../../../components/BusStop/components/Row';

const Row = ({
  adTicketId,
  ackDisplay,
  eaId,
  adt,
  specialtyCode,
  patient,
  priority,
  status,
  busStopStep,
  handlingByHospCode,
  handlingByWardName,
  handlingBySuffix,
  diagnosis,
  remark,
  sex,
  waitTime,
  columnIndex,
  showDetail,
  measure,
  admWardInfo,
  subSpecialtyCode,
  busStopInfo,
  hold,
  handleAck,
  transferRequestId,
  ticketType,
  setSuccessAlert,
  setRefreshTicketList,
  version,
  isHistory,
  ackDate,
  ackBy,
  handleAdmissionVersionError,
  adTicketType,
  admitWard,
  admitSpecialtyOthers,
  specialtyRemark,
  performRedirect,
  showBarcode = false,
  ehAddrInfo,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const { type } = useQuery();

  const handleEdit = useCallback(() => {
    history.push(
      type === 'BS' || ticketType === 'PWA'
        ? `/${'bedstatus'}Form/${eaId}?transferRequestId=${transferRequestId}&actionableMode=1`
        : `/${'admission'}Form/${eaId}?actionableMode=1`
    );
  }, [eaId]);

  const handleConfirmReady = useCallback(async () => {
    try {
      const params = {
        actionType: 9,
        eaId,
        transferRequestId,
        version,
      };
      await eadmissionAPI.editAdmission(params);
      setRefreshTicketList((o) => !o);
      setSuccessAlert(true);
    } catch (error) {
      handleAdmissionVersionError(error);
    }
  }, [eaId, transferRequestId]);

  const handleHanding = useCallback(() => {
    history.push({
      pathname: `/admissionWardAssignment`,
      search: new URLSearchParams({
        id: busStopInfo?.BUS_STOP_ID,
        eaId: eaId,
        gender: sex,
        specialtyCode: specialtyCode,
        subSpecialtyCode: subSpecialtyCode,
        actionableMode: fullScreen ? '1' : null,
      }).toString(),
    });
  }, [busStopInfo, eaId, specialtyCode, subSpecialtyCode, fullScreen]);

  switch (columnIndex) {
    case 0:
      return (
        <Box display="flex" flexDirection="column" gridGap="0.62rem">
          <Box
            bgcolor={WtBg(waitTime)}
            padding="0.5rem 0.5rem 0.5rem 0.5rem"
            borderRadius="0.3rem"
            minWidth="6.5rem"
            maxWidth="6.5rem"
          >
            <Text fontSize="1rem" color="white">
              {conditional(adt, moment(adt).format('YYYY-MM-DD HH:mm'), '')}
            </Text>
          </Box>
          {ticketType === 'PWR' && (
            <StyleButton
              text="Ward Ready"
              backgroundColor="#28D2AB"
              type="fill"
              p="0.88rem"
              width="auto"
              onClick={handleConfirmReady}
            />
          )}
        </Box>
      );
    case 1:
      return (
        <Box display="flex">
          <Box
            textAlign="center"
            bgcolor={WtBg(waitTime)}
            padding={getPaddingByWaitTime(waitTime)}
            borderRadius="0.3rem"
          >
            <Text color="white" fontSize="1rem">
              {conditional(waitTime, admissionUtil.secondsToHHMM(waitTime), '')}
            </Text>
            {/* <sup className={classes.sup}>1</sup> */}
          </Box>
        </Box>
      );
    case 2:
      return (
        <Box display="flex" flexDirection="column" gridGap="0.3rem">
          <Text fontSize="1rem" color="#2E4469">
            {`${initValue(specialtyCode, '')} ${initValue(subSpecialtyCode, '')}`}
          </Text>
          <Box display="flex" gridGap="0.3rem">
            <div className={classes.admit}>{initValue(admitWard, '   ')}</div>
            <div className={classes.admit}>{initValue(admitSpecialtyOthers, '   ')}</div>
          </Box>
          <Text fontSize="0.95rem" color="#B92B21">
            {initValue(specialtyRemark, '')}
          </Text>
        </Box>
      );
    case 3:
      return (
        <PatientUI
          key={`patient-${eaId || adTicketId || patient?.caseNo || ''}`}
          {...patient}
          measure={measure}
          showBarcode={showBarcode}
          ehAddrInfo={ehAddrInfo}
        />
      );
    case 4:
      return (
        <Text fontSize="0.875rem" color="#2E4469">
          {initValue(diagnosis, '')}
        </Text>
      );
    case 5:
      return <Triangle {...getPriorityInfo(priority)} />;
    case 6:
      return <WardUI admWardInfo={admWardInfo} gender={patient.gender} status={status} />;
    case 7:
      return <StatusUI status={status} name={busStopStep} showRedirectIcon={performRedirect} />;
    case 8:
      return (
        <HandingUI
          handlingByHospCode={handlingByHospCode}
          handlingByWardName={handlingByWardName}
          time={handlingBySuffix}
          NEXT_BUS_STOP_STEP={busStopInfo?.NEXT_BUS_STOP_STEP}
          hold={hold}
          WARD_ASSIGN_TITLE={busStopInfo?.WARD_ASSIGN_TITLE}
          handleHanding={handleHanding}
        />
      );
    case 9:
      return (
        <Text fontSize="0.875rem" color="#2E4469">
          {initValue(remark, '')}
        </Text>
      );
    case 10:
      if (isHistory) {
        return <AckInfo ackBy={ackBy} ackDate={ackDate} adTicketType={adTicketType} />;
      }
      return (
        <EditUI
          open={showDetail}
          handleEdit={handleEdit}
          busStopInfo={busStopInfo}
          hold={hold}
          gender={sex}
          eaId={eaId}
        />
      );
    case 11:
      if (isHistory) {
        return (
          <EditUI
            open={showDetail}
            handleEdit={handleEdit}
            busStopInfo={busStopInfo}
            hold={hold}
            gender={sex}
            eaId={eaId}
          />
        );
      }
      return ackDisplay ? (
        <StyleButton text="Ack." width="3.875rem" height="3rem" type="fill" onClick={handleAck(adTicketId)} />
      ) : (
        <div />
      );
    default:
      return <div />;
  }
};

const useStyles = makeStyles((theme) => ({
  admit: {
    backgroundColor: '#F5FAFE',
    borderRadius: '0.25rem',
    padding: '0.08rem 0.35rem',
    fontSize: '0.95rem',
    color: '#7D86A9',
    whiteSpace: 'pre',
  },
}));

// waitTime 高亮
const WtBg = (i) => {
  if (i <= 14400) {
    return '#34c759';
  }
  if (i > 14400 && i <= 28800) {
    return '#feca06';
  }
  if (i > 28800 && i <= 43200) {
    return 'rgba(215, 65, 37)';
  }
  if (i > 43200) {
    return '#000000';
  }
};

const getPaddingByWaitTime = (waitTime) => {
  if (waitTime >= 3600000) return '0.5rem 0.5rem 0.5rem 0.4rem';
  if (waitTime >= 28800) return '0.5rem 0.5rem 0.5rem 0.6rem';
  return '0.5rem 0.5rem';
};

export default Row;
